import { AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppTextInput from "@toothfairy/shared-ui/AppTextInput";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import Constants from "../../../../Constants";
import agentsHelper from "../../../../API/Agents";
import IdCopyView from "../../../../Components/ToothFairyComponents/IdCopyView";
import { isLargeScreen } from "../../../MainViewContainer";
import { useHistory } from "../../../../Router";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import useSections from "../../../../Hooks/useSections";
import {
  BuilderSection,
  PassthroughSection,
  useBuilderDirtyTracking,
} from "./BuilderSections";

const ToothFairyCustomInput = React.forwardRef(
  ({ value, onClick, style }, ref) => (
    <AppTextInput value={value} onClick={onClick} ref={ref} style={style} />
  )
);

const JOB_SECTION_FIELD_MAP = {
  profile: ["name", "description"],
  execution: [
    "type",
    "agentID",
    "routineConfig",
    "forcedPrompt",
    "customPromptID",
  ],
  schedule: ["schedule", "timezone", "isActive", "startDate", "endDate"],
};

const JobSettingsView = ({
  isEdit,
  handleDeletion,
  isBuilderLayout,
  builderActiveSection,
  onBuilderContextChange,
  saveVersion,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const { getActiveWorkspace } = useWorkspaces();
  const { setDisplayedView, displayedOpt, viewRenderer } = useSections();
  const AppToasty = AppToast.useToast();
  const { user } = AppUser.useUser();
  const entityName = "Job";
  const { setFieldValue, values, initialValues, errors, submitCount } =
    AppForms.useFormikContext();
  const { state, dispatch } = AppStateManager.useAppStateManager();

  const _showBuilderSection = (sectionId) =>
    builderActiveSection == null || builderActiveSection === sectionId;
  const SectionRenderer = isBuilderLayout ? BuilderSection : PassthroughSection;

  useBuilderDirtyTracking({
    isBuilderLayout,
    values,
    initialValues,
    saveVersion,
    onBuilderContextChange,
    errors,
    submitCount,
    sectionFieldMap: JOB_SECTION_FIELD_MAP,
  });

  const _title = useCallback(() => {
    return isEdit ? t("settings.list.save") : t("settings.list.create");
  }, [entityName, isEdit, t]);

  const getJobObject = () => {
    return state?.jobs?.find((u) => u?.id === values?.id);
  };

  // Clear prompt selection when agent changes
  useEffect(() => {
    if (!isEdit && values?.agentID) {
      // Check if currently selected prompt is still available
      const availablePrompts = getAvailablePrompts();
      const currentPromptStillAvailable = availablePrompts.find(
        (p) => p.id === values?.customPromptID
      );

      // Clear prompt if it's not available for the new agent
      if (!currentPromptStillAvailable && values?.customPromptID) {
        setFieldValue("customPromptID", "");
      }
    }
  }, [values?.agentID]);

  // Get agents and prompts from state
  const agents = state?.agents?.filter((u) => u?.mode != "voice") || [];
  const customPrompts = state?.customPrompts || [];

  // Filter prompts based on selected agent
  const getAvailablePrompts = () => {
    const selectedAgentId = values?.agentID;
    if (!selectedAgentId) return customPrompts;

    return customPrompts.filter((prompt) => {
      // If no availableToAgents specified, prompt is available to all agents
      if (!prompt.availableToAgents || prompt.availableToAgents.length === 0) {
        return true;
      }
      // Check if the selected agent is in the availableToAgents list
      return prompt.availableToAgents.includes(selectedAgentId);
    });
  };

  const jobTypeOptions = [
    { id: "TASK", name: t("jobs.task") },
    { id: "ROUTINE", name: t("jobs.routine") },
  ];

  const selectedAgent = agents.find((a) => a.id === values?.agentID);
  const isRoutine = values?.type === "ROUTINE";
  const agentSupportsRoutine = selectedAgent?.useInterleavedReasoning === true;
  const agentHasLTM = selectedAgent?.isLongTermMemoryEnabled === true;
  const [showEnableLTMChoice, setShowEnableLTMChoice] = useState(false);
  const [ltmEnablingInProgress, setLtmEnablingInProgress] = useState(false);

  useEffect(() => {
    if (isRoutine) {
      if (
        values?.schedule?.frequency === "MINUTES" &&
        values?.schedule?.interval < 30
      ) {
        setFieldValue("schedule.interval", 30);
      }
      if (
        values?.schedule?.frequency === "MINUTES" &&
        !values?.schedule?.interval
      ) {
        setFieldValue("schedule.interval", 30);
      }
    }
  }, [
    isRoutine,
    values?.type,
    values?.schedule?.frequency,
    values?.schedule?.interval,
  ]);

  // Frequency options
  const frequencyOptions = [
    { id: "DAILY", name: t("settings.list.daily") },
    { id: "WEEKLY", name: t("settings.list.weekly") },
    { id: "MONTHLY", name: t("settings.list.monthly") },
    { id: "YEARLY", name: t("settings.list.yearly") },
    { id: "HOURLY", name: t("settings.list.hourly") },
    { id: "MINUTES", name: t("settings.list.minutes") },
    // { id: "CUSTOM", name: "Custom (Cron Expression)" },
  ];

  // Days of week for weekly scheduling (Monday=0 ... Sunday=6, matching the
  // backend scheduler convention in CronJobs/index.py and the chat parser)
  const daysOfWeek = [
    { id: 0, name: t("settings.list.monday") },
    { id: 1, name: t("settings.list.tuesday") },
    { id: 2, name: t("settings.list.wednesday") },
    { id: 3, name: t("settings.list.thursday") },
    { id: 4, name: t("settings.list.friday") },
    { id: 5, name: t("settings.list.saturday") },
    { id: 6, name: t("settings.list.sunday") },
  ];

  // Priority options
  const priorityOptions = [
    { id: "LOW", name: t("settings.list.low") },
    { id: "MEDIUM", name: t("settings.list.medium") },
    { id: "HIGH", name: t("settings.list.high") },
    { id: "CRITICAL", name: t("settings.list.critical") },
  ];

  // Common timezones
  const timezoneOptions = [
    { id: "UTC", name: "UTC" },
    { id: "America/New_York", name: t("settings.list.easternTime") },
    { id: "America/Chicago", name: t("settings.list.centralTime") },
    { id: "America/Denver", name: t("settings.list.mountainTime") },
    { id: "America/Los_Angeles", name: t("settings.list.pacificTime") },
    { id: "Europe/London", name: t("settings.list.london") },
    { id: "Europe/Paris", name: t("settings.list.paris") },
    { id: "Europe/Berlin", name: t("settings.list.berlin") },
    { id: "Asia/Tokyo", name: t("settings.list.tokyo") },
    { id: "Asia/Shanghai", name: t("settings.list.shanghai") },
    { id: "Australia/Sydney", name: t("settings.list.sydney") },
  ];

  const renderScheduleFields = () => {
    const frequency = values?.schedule?.frequency || "DAILY";

    return (
      <>
        {(frequency === "MINUTES" || frequency === "HOURLY") && (
          <AppForms.AppFormDropDown
            isElevated={false}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
            }}
            showTextInputLabel
            label={t("settings.list.interval")}
            isMandatory
            name="schedule.interval"
            value={values?.schedule?.interval}
            items={
              frequency === "MINUTES"
                ? [
                    ...(isRoutine
                      ? []
                      : [
                          { id: 5, name: t("settings.list.fiveMinutes") },
                          { id: 10, name: t("settings.list.tenMinutes") },
                          { id: 15, name: t("settings.list.fifteenMinutes") },
                        ]),
                    { id: 30, name: t("settings.list.thirtyMinutes") },
                    { id: 60, name: t("settings.list.sixtyMinutes") },
                  ]
                : Array.from({ length: 24 }, (_, i) => ({
                    id: i + 1,
                    name: `${i + 1} ${
                      i === 0
                        ? t("settings.list.hour")
                        : t("settings.list.hours")
                    }`,
                  }))
            }
            propertyLabel="name"
            propertyValue="id"
          />
        )}

        {frequency === "WEEKLY" && (
          <AppForms.AppFormDropDown
            isElevated={false}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
            }}
            showTextInputLabel
            label={t("settings.list.dayOfWeek")}
            isMandatory
            name="schedule.dayOfWeek"
            value={values?.schedule?.dayOfWeek}
            items={daysOfWeek}
            propertyLabel="name"
            propertyValue="id"
          />
        )}

        {frequency === "MONTHLY" && (
          <AppForms.AppFormDropDown
            isElevated={false}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
            }}
            showTextInputLabel
            label={t("settings.list.dayOfMonth")}
            isMandatory
            name="schedule.dayOfMonth"
            value={values?.schedule?.dayOfMonth}
            items={Array.from({ length: 31 }, (_, i) => ({
              id: i + 1,
              name: (i + 1).toString(),
            }))}
            propertyLabel="name"
            propertyValue="id"
          />
        )}

        {(frequency === "DAILY" ||
          frequency === "WEEKLY" ||
          frequency === "MONTHLY" ||
          frequency === "YEARLY") && (
          <View style={{ flexDirection: "row", gap: 10 }}>
            <View style={{ flex: 1 }}>
              <AppForms.AppFormDropDown
                isElevated={false}
                dropDownStyle={{
                  borderColor: currentTheme?.theme?.border_color,
                }}
                showTextInputLabel
                label={t("settings.list.hour")}
                isMandatory
                name="schedule.hour"
                value={values?.schedule?.hour}
                items={Array.from({ length: 24 }, (_, i) => ({
                  id: i,
                  name: i.toString().padStart(2, "0"),
                }))}
                propertyLabel="name"
                propertyValue="id"
              />
            </View>
            <View style={{ flex: 1 }}>
              <AppForms.AppFormDropDown
                isElevated={false}
                dropDownStyle={{
                  borderColor: currentTheme?.theme?.border_color,
                }}
                showTextInputLabel
                label={t("settings.list.minute")}
                isMandatory
                name="schedule.minute"
                value={values?.schedule?.minute}
                items={[
                  { id: 0, name: "0" },
                  { id: 15, name: "15" },
                  { id: 30, name: "30" },
                  { id: 45, name: "45" },
                ]}
                propertyLabel="name"
                propertyValue="id"
              />
            </View>
          </View>
        )}

        {frequency === "CUSTOM" && (
          <AppForms.AppFormField
            showTextInputLabel
            isMandatory
            label={t("settings.list.cronExpression")}
            placeholder="0 9 * * MON-FRI"
            name="schedule.cronExpression"
            wrapperInputStyle={{ paddingTop: 10 }}
          />
        )}
      </>
    );
  };

  return (
    <>
      {_showBuilderSection("profile") && (
        <SectionRenderer
          title={t("jobs.builderSectionProfile", {
            defaultValue: "Overview",
          })}
          icon={AppAssets.icons.persona}
        >
          {isEdit && <IdCopyView id={getJobObject()?.id} />}

          <AppForms.AppFormField
            showTextInputLabel
            isMandatory
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            label={t("settings.list.jobName")}
            placeholder={t("settings.list.jobNamePlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="name"
          />
          <AppForms.AppFormField
            showTextInputLabel
            wrapperInputStyle={{
              height: 150,
              marginBottom: 10,
              padding: 5,
            }}
            textContainer={{
              padding: 10,
              marginHorizontal: 0,
              minHeight: 120,
            }}
            multiline
            label={t("settings.list.description")}
            placeholder={t("settings.list.jobDescriptionPlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="description"
          />
        </SectionRenderer>
      )}
      {_showBuilderSection("execution") && (
        <SectionRenderer
          title={t("jobs.builderSectionExecution", {
            defaultValue: "Execution",
          })}
          icon={AppAssets.icons.bolt}
        >
          <AppForms.AppFormDropDown
            isElevated={false}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
            }}
            showTextInputLabel
            label={t("jobs.jobType")}
            isMandatory
            name="type"
            value={values?.type || "TASK"}
            items={jobTypeOptions}
            propertyLabel="name"
            propertyValue="id"
          />
          <AppText
            leftIcon={AppAssets?.icons?.info}
            wrapperStyle={{
              paddingHorizontal: 10,
            }}
            color={currentTheme?.theme?.orange}
            style={{
              marginLeft: 10,
            }}
            iconSize={14}
          >
            {isRoutine ? t("jobs.routineDesc") : t("jobs.taskDesc")}
          </AppText>

          {isRoutine && values?.agentID && !agentSupportsRoutine && (
            <View
              style={{
                marginHorizontal: 10,
                marginBottom: 10,
                padding: 10,
                backgroundColor: currentTheme?.theme?.orange + "22",
                borderRadius: 8,
              }}
            >
              <AppText
                style={{ color: currentTheme?.theme?.orange, fontSize: 13 }}
              >
                {t("jobs.routineRequiresInterleaved")}
              </AppText>
            </View>
          )}

          {isRoutine &&
            values?.agentID &&
            agentSupportsRoutine &&
            !agentHasLTM && (
              <View
                style={{
                  marginHorizontal: 10,
                  marginBottom: 10,
                  padding: 10,
                  backgroundColor: currentTheme?.theme?.orange + "22",
                  borderRadius: 8,
                }}
              >
                <AppText
                  style={{
                    color: currentTheme?.theme?.orange,
                    fontSize: 13,
                    marginBottom: 8,
                  }}
                >
                  {t("jobs.routineRequiresLTM")}
                </AppText>
                <ToothFairyButton
                  title={t("jobs.enableLTM")}
                  onPress={() => setShowEnableLTMChoice(true)}
                  disabled={ltmEnablingInProgress}
                  style={{ marginTop: 5 }}
                />
              </View>
            )}

          <AppForms.AppFormDropDown
            isElevated={false}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
            }}
            showTextInputLabel
            label={t("settings.list.agent")}
            isMandatory
            name="agentID"
            value={values?.agentID}
            items={[
              { id: "", name: t("settings.list.selectAgent") },
              ...agents
                .filter((agent) =>
                  isRoutine ? agent.useInterleavedReasoning : true
                )
                .map((agent) => ({
                  id: agent.id,
                  name: agent.label,
                })),
            ]}
            propertyLabel="name"
            propertyValue="id"
          />

          {isRoutine && (
            <AppForms.AppFormDropDown
              isElevated={false}
              dropDownStyle={{
                borderColor: currentTheme?.theme?.border_color,
              }}
              showTextInputLabel
              label={t("jobs.maxPlannedActions")}
              name="routineConfig.maxPlannedActions"
              value={values?.routineConfig?.maxPlannedActions || 5}
              items={[
                { id: 1, name: "1" },
                { id: 3, name: "3" },
                { id: 5, name: "5" },
                { id: 10, name: "10" },
                { id: 15, name: "15" },
                { id: 20, name: "20" },
              ]}
              propertyLabel="name"
              propertyValue="id"
            />
          )}

          {values?.forcedPrompt ? (
            <AppForms.AppFormField
              showTextInputLabel
              wrapperInputStyle={{
                paddingTop: 10,
                padding: 5,
              }}
              multiline={true}
              textContainer={{
                minHeight: 120,
                paddingVertical: 10,
                marginHorizontal: 0,
              }}
              label={t("settings.list.forcedPrompt")}
              placeholder={t("settings.list.forcedPromptPlaceholder")}
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              maxCharacters={1024}
              value={values?.forcedPrompt}
              name="forcedPrompt"
            />
          ) : values?.agentID && getAvailablePrompts().length === 0 ? (
            <View style={{ marginTop: 10, marginBottom: 20 }}>
              <AppText
                style={{
                  fontWeight: "bold",
                  marginBottom: 10,
                  marginLeft: 10,
                }}
              >
                {t("settings.list.prompt")}
              </AppText>
              <AppPlaceholderView
                wrapperStyle={{
                  marginLeft: 5,
                  minHeight: 150,
                  backgroundColor: currentTheme?.theme?.lightGrey,
                  borderRadius: 8,
                  padding: 20,
                  marginLeft: 5,
                  marginRight: 5,
                  marginHorizontal: 5,
                  width: "auto",
                }}
                size={25}
                icon={AppAssets.icons.orbit}
                mainCaption={t("settings.list.noPromptsAvailable")}
                subCaption={t("settings.list.createPromptForAgent")}
                isClickable={true}
                onPress={() => {
                  setDisplayedView("GENERATOR_SETTINGS");
                  history.push(
                    `/workspaces/${
                      getActiveWorkspace()?.id
                    }/settings/generator_settings`
                  );
                }}
              />
            </View>
          ) : (
            <AppForms.AppFormDropDown
              isElevated={false}
              dropDownStyle={{
                borderColor: currentTheme?.theme?.border_color,
              }}
              showTextInputLabel
              label={t("settings.list.prompt")}
              isMandatory
              name="customPromptID"
              value={values?.customPromptID}
              items={[
                {
                  id: "",
                  name: !values?.agentID
                    ? t("settings.list.selectAgentFirst")
                    : t("settings.list.selectPrompt"),
                },
                ...getAvailablePrompts().map((prompt) => ({
                  id: prompt.id,
                  name: prompt.interpolationString,
                })),
              ]}
              propertyLabel="name"
              propertyValue="id"
              disabled={!values?.agentID}
            />
          )}
        </SectionRenderer>
      )}
      {_showBuilderSection("schedule") && (
        <SectionRenderer
          title={t("jobs.builderSectionSchedule", {
            defaultValue: "Schedule",
          })}
          icon={AppAssets.icons.clock}
        >
          <AppForms.AppFormSwitch
            wrapperStyle={[
              {
                // backgroundColor: isEdit
                //   ? currentTheme?.theme?.grey
                //   : currentTheme?.theme?.lightGrey,
                maxHeight: 30,
                paddingHorizontal: 0,
                paddingVertical: 18,
                margin: 5,
                marginTop: 0,
                marginBottom: 30,
              },
            ]}
            labelTextStyle={{
              fontSize: currentTheme?.theme?.small_font_size,
              color: currentTheme?.theme?.darkGrey,
            }}
            showTextInputLabel={true}
            label={t("settings.list.active")}
            displayIsTicked={true}
            name="isActive"
            value={values?.isActive}
            description={t("settings.list.activeDesc")}
            descriptionIcon={false}
            descriptionColor={currentTheme?.theme?.mediumGrey}
          />

          <AppForms.AppFormDropDown
            isElevated={false}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
            }}
            showTextInputLabel
            label={t("settings.list.frequency")}
            isMandatory
            name="schedule.frequency"
            value={values?.schedule?.frequency}
            items={frequencyOptions}
            propertyLabel="name"
            propertyValue="id"
          />

          {renderScheduleFields()}

          <AppForms.AppFormDropDown
            isElevated={false}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
            }}
            showTextInputLabel
            label={t("settings.list.timezone")}
            isMandatory
            name="timezone"
            value={values?.timezone}
            items={timezoneOptions}
            propertyLabel="name"
            propertyValue="id"
          />

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              paddingBottom: 0,
              paddingVertical: 0,
              marginTop: 10,
              zIndex: 1,
              flexWrap: "wrap",
              maxWidth: !isLargeScreen() && 340,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginLeft: 10,
              }}
            >
              <AppText
                wrapperStyle={{ marginRight: 10 }}
                style={{ fontWeight: "bold" }}
              >
                {t("settings.list.startDate")}
              </AppText>
              <DatePicker
                customInput={
                  <ToothFairyCustomInput style={{ fontWeight: "bold" }} />
                }
                selected={values?.startDate ? new Date(values.startDate) : null}
                onChange={(date) =>
                  setFieldValue("startDate", date ? date.toISOString() : null)
                }
                dateFormat="dd/MM/yyyy"
                placeholderText={t("settings.list.optional")}
              />
            </View>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginLeft: isLargeScreen() && 20,
              }}
            >
              <AppText
                wrapperStyle={{ marginRight: 10 }}
                style={{ fontWeight: "bold" }}
              >
                {t("settings.list.endDate")}
              </AppText>
              <DatePicker
                customInput={
                  <ToothFairyCustomInput style={{ fontWeight: "bold" }} />
                }
                selected={values?.endDate ? new Date(values.endDate) : null}
                onChange={(date) =>
                  setFieldValue("endDate", date ? date.toISOString() : null)
                }
                dateFormat="dd/MM/yyyy"
                placeholderText={t("settings.list.optional")}
              />
            </View>
          </View>

          {/* Advanced Configuration */}
          {/* <AppText
          style={{
            fontWeight: "bold",
            fontSize: 16,
            marginTop: 20,
            marginBottom: 10,
            marginLeft: 10,
          }}
        >
          Advanced Configuration (Optional)
        </AppText>
        <View style={{ flexDirection: "row", gap: 10 }}>
          <View style={{ flex: 1 }}>
            <AppForms.AppFormDropDown
              isElevated={false}
              dropDownStyle={{
                borderColor: currentTheme?.theme?.border_color,
              }}
              showTextInputLabel
              label="Timeout"
              name="executionConfig.timeout"
              value={values?.executionConfig?.timeout}
              items={[
                { id: 30, name: "30 seconds" },
                { id: 60, name: "1 minute" },
                { id: 120, name: "2 minutes" },
                { id: 300, name: "5 minutes" },
                { id: 600, name: "10 minutes" },
                { id: 900, name: "15 minutes" },
                { id: 1800, name: "30 minutes" },
                { id: 3600, name: "1 hour" },
              ]}
              propertyLabel="name"
              propertyValue="id"
            />
          </View>
          <View style={{ flex: 1 }}>
            <AppForms.AppFormDropDown
              isElevated={false}
              dropDownStyle={{
                borderColor: currentTheme?.theme?.border_color,
              }}
              showTextInputLabel
              label="Priority"
              name="executionConfig.priority"
              value={values?.executionConfig?.priority}
              items={priorityOptions}
              propertyLabel="name"
              propertyValue="id"
            />
          </View>
        </View>

        <AppText
          style={{
            fontWeight: "bold",
            fontSize: 14,
            marginTop: 15,
            marginBottom: 5,
            marginLeft: 10,
          }}
        >
          Notifications
        </AppText>
        <AppText
          style={{
            fontWeight: "bold",
            fontSize: 14,
            marginTop: 15,
            marginBottom: 5,
            marginLeft: 10,
          }}
        >
          Retry Configuration
        </AppText>
        <View style={{ flexDirection: "row", gap: 10 }}>
          <View style={{ flex: 1 }}>
            <AppForms.AppFormDropDown
              isElevated={false}
              dropDownStyle={{
                borderColor: currentTheme?.theme?.border_color,
              }}
              showTextInputLabel
              label="Max Retries"
              name="retryConfig.maxRetries"
              value={values?.retryConfig?.maxRetries}
              items={[
                { id: 0, name: "No retries" },
                { id: 1, name: "1 retry" },
                { id: 2, name: "2 retries" },
                { id: 3, name: "3 retries" },
                { id: 5, name: "5 retries" },
                { id: 10, name: "10 retries" },
              ]}
              propertyLabel="name"
              propertyValue="id"
            />
          </View>
          <View style={{ flex: 1 }}>
            <AppForms.AppFormDropDown
              isElevated={false}
              dropDownStyle={{
                borderColor: currentTheme?.theme?.border_color,
              }}
              showTextInputLabel
              label="Retry Interval"
              name="retryConfig.retryInterval"
              value={values?.retryConfig?.retryInterval}
              items={[
                { id: 10, name: "10 seconds" },
                { id: 30, name: "30 seconds" },
                { id: 60, name: "1 minute" },
                { id: 120, name: "2 minutes" },
                { id: 300, name: "5 minutes" },
                { id: 600, name: "10 minutes" },
              ]}
              propertyLabel="name"
              propertyValue="id"
            />
          </View>
        </View> */}
          {/* <AppText
          leftIcon={AppAssets?.icons?.info}
          wrapperStyle={{
            paddingHorizontal: 10,
            marginTop: 20,
          }}
          color={currentTheme?.theme?.orange}
          style={{
            marginLeft: 10,
            textDecorationColor: currentTheme?.theme?.orange,
          }}
          iconSize={14}
        >
          Webhook configuration will be available in a future update
        </AppText> */}
        </SectionRenderer>
      )}
      {!isBuilderLayout && (
        <View style={{ alignItems: "center", zIndex: -1 }}>
          <AppForms.AppSubmitButton
            title={_title()}
            awesomeIconColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.primary}
            btnContentStyle={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            style={[
              {
                height: Constants.appButtonHeight,
                width: "auto",
                shadowColor: currentTheme?.theme?.transparent,
                marginHorizontal: 10,
              },
            ]}
            wrapperStyle={[
              {
                width: "fit-content",
                minWidth: 150,
                marginTop: 20,
                height: Constants.appButtonHeight,
              },
            ]}
          />

          {isEdit && (
            <ToothFairyButton
              title={t("settings.list.delete")}
              onPress={() => handleDeletion(getJobObject()?.id)}
              isDeletion
              wrapperStyle={{
                marginTop: 30,
                minWidth: 150,
                height: Constants.appButtonHeight,
              }}
            />
          )}
        </View>
      )}

      <ToothFairyChoice
        showChoice={showEnableLTMChoice}
        setShowChoice={setShowEnableLTMChoice}
        caption={t("jobs.enableLTMCaption")}
        subCaption={t("jobs.enableLTMSubCaption")}
        confirmCaption={t("jobs.enableLTM")}
        cancelCaption={t("settings.list.cancel")}
        onConfirmButtonPress={() => {
          setShowEnableLTMChoice(false);
          const updatedAgents = state.agents.map((a) =>
            a.id === values?.agentID
              ? { ...a, isLongTermMemoryEnabled: true }
              : a
          );
          dispatch("SET_AGENTS", updatedAgents);
          agentsHelper
            .updateAgent(
              values?.agentID,
              [{ property: "isLongTermMemoryEnabled", value: true }],
              user?.attributes?.sub || null
            )
            .then(() => {
              AppToasty.show(t("jobs.ltmEnabledSuccess"), { type: "success" });
            })
            .catch((e) => {
              AppToasty.show(t("jobs.ltmEnabledFailed"), { type: "danger" });
            });
        }}
        onCancelButtonPress={() => setShowEnableLTMChoice(false)}
      />
    </>
  );
};

export default JobSettingsView;
